<template>
  <div class="base-history-filters">
    <div class="base-history-filters__row">
      <a-input-search
        v-model="search"
        :placeholder="searchPlaceholder"
        class="base-history-filters__search"
        allow-clear
        @search="emitChange"
        @change="onInputChange"
      >
        <a-icon slot="prefix" type="search" style="color: rgba(0, 0, 0, 0.25)" />
      </a-input-search>

      <a-button
        class="base-history-filters__btn"
        :class="{ 'base-history-filters__btn--active': filtersOpen }"
        @click="toggleFilters"
      >
        <a-icon type="control" /> Filtros
        <span v-if="activeCount" class="base-history-filters__badge">
          {{ activeCount }}
        </span>
      </a-button>
    </div>

    <div v-show="filtersOpen" class="base-history-filters__panel">
      <div class="base-history-filters__group">
        <span class="base-history-filters__label">Tipo de alteração</span>
        <a-checkbox-group
          v-model="draftEvents"
          class="base-history-filters__checks"
        >
          <a-checkbox
            v-for="opt in eventOptions"
            :key="opt.value"
            :value="opt.value"
          >
            {{ opt.label }}
          </a-checkbox>
        </a-checkbox-group>
      </div>

      <div class="base-history-filters__group">
        <span class="base-history-filters__label">Período</span>
        <a-range-picker
          v-model="draftPeriod"
          :locale="datePickerLocale"
          :format="DATE_DISPLAY_FORMAT"
          :placeholder="['Data inicial', 'Data final']"
          :get-calendar-container="getContainer"
          class="base-history-filters__range"
        />
      </div>

      <div class="base-history-filters__group">
        <span class="base-history-filters__label">Usuário</span>
        <a-select
          v-model="draftUserId"
          allow-clear
          placeholder="Todos os usuários"
          class="base-history-filters__select"
          :get-popup-container="getContainer"
        >
          <a-select-option
            v-for="u in users"
            :key="u.userId"
            :value="u.userId"
          >
            {{ u.name || "Sem usuário" }}
          </a-select-option>
        </a-select>
      </div>

      <div class="base-history-filters__footer">
        <a-button size="small" @click="clearFilters">Limpar</a-button>
        <a-button size="small" type="primary" @click="applyFilters">
          Aplicar
        </a-button>
      </div>
    </div>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import "moment/locale/pt-br";
import ptBR from "ant-design-vue/lib/date-picker/locale/pt_BR";

const DATE_DISPLAY_FORMAT = "DD/MM/YYYY";
const DATE_PARAM_FORMAT = "YYYY-MM-DD";

export default {
  name: "BaseHistoryFilters",
  props: {
    users: {
      type: Array,
      default: () => [],
    },
    eventOptions: {
      type: Array,
      default: () => [],
    },
    searchPlaceholder: {
      type: String,
      default: "Busque pelo usuário ou data",
    },
  },
  data() {
    return {
      search: "",
      filtersOpen: false,
      events: [],
      userId: undefined,
      period: [],
      draftEvents: [],
      draftUserId: undefined,
      draftPeriod: [],
      datePickerLocale: ptBR,
      DATE_DISPLAY_FORMAT,
    };
  },
  computed: {
    activeCount() {
      return (
        this.events.length +
        (this.userId ? 1 : 0) +
        (this.period.length === 2 ? 1 : 0)
      );
    },
  },
  created() {
    this.debouncedEmit = debounce(this.emitChange, 500);
  },
  beforeDestroy() {
    this.debouncedEmit.cancel();
  },
  methods: {
    getContainer(node) {
      return node.parentNode;
    },
    onInputChange() {
      this.debouncedEmit();
    },
    toggleFilters() {
      if (!this.filtersOpen) {
        this.draftEvents = this.events.slice();
        this.draftUserId = this.userId;
        this.draftPeriod = this.period.slice();
      }
      this.filtersOpen = !this.filtersOpen;
    },
    applyFilters() {
      this.events = this.draftEvents.slice();
      this.userId = this.draftUserId;
      this.period = this.draftPeriod.slice();
      this.filtersOpen = false;
      this.emitChange();
    },
    clearFilters() {
      this.draftEvents = [];
      this.draftUserId = undefined;
      this.draftPeriod = [];
      this.events = [];
      this.userId = undefined;
      this.period = [];
      this.filtersOpen = false;
      this.emitChange();
    },
    emitChange() {
      this.$emit("change", {
        search: (this.search || "").trim(),
        events: this.events,
        userId: this.userId || null,
        dateFrom:
          this.period.length === 2
            ? this.period[0].format(DATE_PARAM_FORMAT)
            : null,
        dateTo:
          this.period.length === 2
            ? this.period[1].format(DATE_PARAM_FORMAT)
            : null,
      });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.base-history-filters {
  margin-bottom: 20px;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  &__search {
    flex: 0 1 @details-search-width;
    max-width: 100%;
    min-width: 0;

    /deep/ .ant-input {
      border-radius: 6px;
    }
  }

  &__btn {
    flex: none;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    border-radius: 6px;
    color: @color-text-medium;

    &--active {
      color: @color-primary;
      border-color: @color-primary;
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: @color-primary;
    font-size: 11px;
    line-height: 1;
    color: #fff;
  }

  &__panel {
    margin-top: 12px;
    padding: 16px;
    border: 1px solid @color-border-light;
    border-radius: 8px;
    background: @color-bg-panel;
  }

  &__group {
    margin-bottom: 16px;
  }

  &__label {
    display: block;
    margin-bottom: 8px;
    font-size: 13px;
    font-weight: 600;
    color: @color-text-dark;
  }

  &__checks {
    display: flex;
    flex-direction: column;
    gap: 8px;

    /deep/ .ant-checkbox-wrapper {
      margin-left: 0;
    }
  }

  &__range,
  &__select {
    width: 100%;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
